import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabaseClient';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';
import emailService from '../services/emailService';

// Types for our dashboard metrics
interface DashboardMetrics {
  totalSentToday: number;
  dailyLimit: number;
  activeCampaigns: number;
  openRate: number;
  replyRate: number;
  bounceRate: number;
  totalLeads: number;
  leadsToday: number;
  leadConversionRate: number;
  averageLeadValue: number;
  totalLeadValue: number;
  positiveResponses: number;
  positiveResponsesToday: number;
  positiveResponseRate: number;
}

const EmailDashboard: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    totalSentToday: 0,
    dailyLimit: 100,
    activeCampaigns: 0,
    openRate: 0,
    replyRate: 0,
    bounceRate: 0,
    totalLeads: 0,
    leadsToday: 0,
    leadConversionRate: 0,
    averageLeadValue: 0,
    totalLeadValue: 0,
    positiveResponses: 0,
    positiveResponsesToday: 0,
    positiveResponseRate: 0,
  });
  const [dailyData, setDailyData] = useState<{ date: string; sent: number }[]>([]);
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchDashboardData();
    }
  }, [user]);

  const fetchDashboardData = async () => {
    setIsLoading(true);
    try {
      // Fetch metrics data
      const metricsData = await fetchMetrics();
      setMetrics(metricsData);
      
      // Fetch daily activity data
      const dailySentData = await fetchDailyActivity();
      setDailyData(dailySentData);
      
      // Fetch recent activity
      const activityData = await fetchRecentActivity();
      setRecentActivity(activityData);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMetrics = async (): Promise<DashboardMetrics> => {
    try {
      const response = await emailService.getDashboardMetrics();
      return response;
    } catch (error) {
      console.error('Error fetching metrics:', error);
      toast.error('Failed to load dashboard metrics');
      // Return default values if API call fails
      return {
        totalSentToday: 0,
        dailyLimit: 100,
        activeCampaigns: 0,
        openRate: 0,
        replyRate: 0,
        bounceRate: 0,
        totalLeads: 0,
        leadsToday: 0,
        leadConversionRate: 0,
        averageLeadValue: 0,
        totalLeadValue: 0,
        positiveResponses: 0,
        positiveResponsesToday: 0,
        positiveResponseRate: 0,
      };
    }
  };

  const fetchDailyActivity = async () => {
    try {
      const response = await emailService.getDailyActivity();
      return response;
    } catch (error) {
      console.error('Error fetching daily activity:', error);
      toast.error('Failed to load daily activity data');
      
      // Return empty array if API call fails
      return [];
    }
  };

  const fetchRecentActivity = async () => {
    try {
      const response = await emailService.getRecentActivity();
      return response;
    } catch (error) {
      console.error('Error fetching recent activity:', error);
      toast.error('Failed to load recent activity data');
      
      // Return empty array if API call fails
      return [];
    }
  };

  const formatTimestamp = (timestamp: number) => {
    const now = new Date().getTime();
    const diff = now - timestamp;
    
    // Convert to minutes
    const minutes = Math.floor(diff / (1000 * 60));
    
    if (minutes < 1) {
      return 'Just now';
    } else if (minutes < 60) {
      return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    } else {
      const hours = Math.floor(minutes / 60);
      return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'sent':
        return '📤';
      case 'opened':
        return '👁️';
      case 'replied':
        return '↩️';
      case 'bounced':
        return '❌';
      case 'positive_response':
        return '✅';
      default:
        return '📧';
    }
  };

  const getActivityText = (activity: any) => {
    switch (activity.type) {
      case 'sent':
        return `Email sent to ${activity.email}`;
      case 'opened':
        return `${activity.email} opened your email`;
      case 'replied':
        return `${activity.email} replied to your email`;
      case 'bounced':
        return `Email to ${activity.email} bounced`;
      case 'positive_response':
        return `${activity.email} sent a positive response${activity.reason ? ': ' + activity.reason : ''}`;
      default:
        return `Activity with ${activity.email}`;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-xl font-semibold mb-6">Email Dashboard</h2>

      {/* Key Metrics Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-2">Today's Emails</h2>
          <div className="flex items-end">
            <span className="text-3xl font-bold">{metrics.totalSentToday}</span>
            <span className="text-gray-500 ml-1">/ {metrics.dailyLimit}</span>
          </div>
          <div className="mt-2 h-2 bg-gray-200 rounded-full">
            <div 
              className="h-2 bg-blue-600 rounded-full" 
              style={{ width: `${(metrics.totalSentToday / metrics.dailyLimit) * 100}%` }}
            ></div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-2">Active Campaigns</h2>
          <div className="text-3xl font-bold">{metrics.activeCampaigns}</div>
          <p className="text-sm text-gray-500 mt-1">Currently running</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-2">Response Metrics</h2>
          <div className="flex justify-between mt-2 space-x-4">
            <div>
              <p className="text-sm text-gray-500">Opens</p>
              <p className="text-xl font-semibold">{metrics.openRate}%</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Replies</p>
              <p className="text-xl font-semibold">{metrics.replyRate}%</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Bounces</p>
              <p className="text-xl font-semibold">{metrics.bounceRate}%</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-2">Positive Responses</h2>
          <div className="flex items-end">
            <span className="text-3xl font-bold text-green-600">{metrics.positiveResponses}</span>
            <span className="text-gray-500 ml-2 mb-1">Total</span>
          </div>
          <div className="flex justify-between mt-3">
            <div>
              <p className="text-sm text-gray-500">Today</p>
              <p className="text-xl font-semibold text-green-600">{metrics.positiveResponsesToday}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Rate</p>
              <p className="text-xl font-semibold">{metrics.positiveResponseRate}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Daily Activity Graph */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-lg font-semibold mb-4">Daily Activity</h2>
        <div className="h-64">
          {/* In a real app, we would use a chart library like Chart.js */}
          <div className="flex h-full items-end space-x-1">
            {dailyData.map((day, index) => (
              <div 
                key={index} 
                className="flex flex-col items-center flex-1"
              >
                <div 
                  className={`w-full ${
                    day.sent > 40 ? 'bg-red-500' : 'bg-blue-500'
                  } rounded-t`}
                  style={{ height: `${(day.sent / 50) * 100}%` }}
                ></div>
                {index % 5 === 0 && (
                  <span className="text-xs text-gray-500 mt-1">
                    {day.date.split('-')[2]}/{day.date.split('-')[1]}
                  </span>
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2">
            <span className="text-xs text-gray-500">30 days ago</span>
            <span className="text-xs text-gray-500">Today</span>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Recent Activity</h2>
        <div className="space-y-4">
          {recentActivity.map(activity => (
            <div key={activity.id} className="flex items-start">
              <div className="bg-gray-100 rounded-full p-2 mr-3">
                <span>{getActivityIcon(activity.type)}</span>
              </div>
              <div className="flex-1">
                <p className="font-medium">{getActivityText(activity)}</p>
                <div className="flex text-sm text-gray-500">
                  <span>{activity.campaign}</span>
                  <span className="mx-1">•</span>
                  <span>{formatTimestamp(activity.timestamp)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 text-center">
          <Link to="/email" className="text-blue-600 hover:underline">View all activity</Link>
        </div>
      </div>
    </div>
  );
};

export default EmailDashboard;
